export const API_BASE_URL = "https://api-dear-lottery.threecircle5.com/api"
// export const API_BASE_URL = "http://localhost:5050"
export interface ApiResponse<T> {
  success: boolean
  data?: T
  error?: string
}

export interface Session {
  id: string
  name: string
  drawTime: string
  active: boolean
  date: string
  stats?: {
    singleBets: number
    rajshreeBets: number
    choiceBets: number
    results: number
  }
  results?: LotteryResult[]
}

export interface SingleBooking {
  id: string
  userId: string
  number: number
  qty: number
  amount: number
  status: string
  sessionId: string
  createdAt: string
  user?: {
    userId: string
    name: string
  }
  session?: {
    name: string
    date: string
  }
}

export interface RajshreeBooking {
  id: string
  userId: string
  series: string
  number: number
  qty: number
  amount: number
  status: string
  sessionId: string
  createdAt: string
  user?: {
    userId: string
    name: string
  }
  session?: {
    name: string
    date: string
  }
}

export interface ChoiceBooking {
  id: string
  userId: string
  numbers: string
  qty: number
  amount: number
  status: string
  sessionId: string
  createdAt: string
  user?: {
    userId: string
    name: string
  }
  session?: {
    name: string
    date: string
  }
}

export interface LotteryResult {
  id: string
  sessionId: string
  category: string
  number: number
  createdAt: string
}

export interface User {
  id: string
  userId: string
  name?: string
  balance: number
  role: string
  isActive: boolean
  createdAt?: string
  updatedAt?: string
}

export interface CreateUserRequest {
  userId: string
  pin: string
  name: string
  role: "USER" | "ADMIN"
}

export interface SingleBookingRequest {
  sessionId: string
  number: number
  qty: number
  amount: number
}

export interface RajshreeBookingRequest {
  sessionId: string
  series: string
  number: number
  qty: number
  amount: number
}

export interface ChoiceBookingRequest {
  sessionId: string
  numbers: string
  qty: number
  amount: number
}

export interface ChoiceRangeBookingRequest {
  sessionId: string
  item: string
  from: string
  to: string
  tickets: string
  qty: number
  amount: number
}

export interface ChoiceRangeBooking {
  id: string
  userId: string
  sessionId: string
  item: string
  from: string
  to: string
  tickets: string
  qty: number
  amount: number
  status: string
  createdAt: string
  user?: {
    userId: string
    name: string
  }
  session?: {
    name: string
    date: string
  }
}

export interface ReportData {
  singles?: SingleBooking[]
  rajshrees?: RajshreeBooking[]
  choices?: ChoiceBooking[]
  choicesRange?: ChoiceRangeBooking[]
}
// Add this to your existing apiService
export const apiServices = {
  // ... your existing methods ...
  
  createChoiceRangeBooking: async (bookingData: {
    sessionId: string;
    item: string;
    from: string;
    to: string;
    tickets: string;
    qty: number;
    amount: number;
  }): Promise<{ success: boolean; error?: string }> => {
    try {
      const token = localStorage.getItem('auth_token');
      
      // Format numbers as "from-to" string
      const numbers = `${bookingData.from}-${bookingData.to}`;
      
      const response = await fetch(`${API_BASE_URL}/bookings/choice`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          numbers,
          qty: bookingData.qty,
          amount: bookingData.amount,
          sessionId: bookingData.sessionId,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: result.error || 'Failed to place choice booking'
        };
      }

      return { success: true };
    } catch (error) {
      console.error('Error creating choice booking:', error);
      return {
        success: false,
        error: 'Network error occurred'
      };
    }
  },
};
class ApiService {
  private getAuthHeaders() {
    const token = localStorage.getItem('auth_token')
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    }
  }

  private async handleResponse<T>(response: Response): Promise<ApiResponse<T>> {
    if (!response.ok) {
      const error = await response.text()
      return { success: false, error }
    }
    const data = await response.json()
    return { success: true, data }
  }

  // ========== SESSION APIs ==========
  async getActiveSession(): Promise<ApiResponse<Session>> {
    const response = await fetch(`${API_BASE_URL}/sessions/active`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<Session>(response)
  }

  async getAllSessions(): Promise<ApiResponse<Session[]>> {
    const response = await fetch(`${API_BASE_URL}/sessions/list`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<Session[]>(response)
  }

  async createSession(name: string, drawTime: string): Promise<ApiResponse<Session>> {
    const response = await fetch(`${API_BASE_URL}/sessions/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ name, drawTime }),
    })
    return this.handleResponse<Session>(response)
  }

  async updateSessionStatus(sessionId: string, active: boolean): Promise<ApiResponse<Session>> {
    const response = await fetch(`${API_BASE_URL}/sessions/update`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sessionId, active }),
    })
    return this.handleResponse<Session>(response)
  }

  // ========== BOOKING APIs ==========
  async createSingleBooking(booking: SingleBookingRequest): Promise<ApiResponse<SingleBooking>> {
    const response = await fetch(`${API_BASE_URL}/single/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(booking),
    })
    return this.handleResponse<SingleBooking>(response)
  }

  async createRajshreeBooking(booking: RajshreeBookingRequest): Promise<ApiResponse<RajshreeBooking>> {
    const response = await fetch(`${API_BASE_URL}/rajshree/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(booking),
    })
    return this.handleResponse<RajshreeBooking>(response)
  }

  async createChoiceBooking(booking: ChoiceBookingRequest): Promise<ApiResponse<ChoiceBooking>> {
    const response = await fetch(`${API_BASE_URL}/choice/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(booking),
    })
    return this.handleResponse<ChoiceBooking>(response)
  }

  async createChoiceRangeBooking(booking: ChoiceRangeBookingRequest): Promise<ApiResponse<ChoiceRangeBooking>> {
    const response = await fetch(`${API_BASE_URL}/choice-range/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(booking),
    })
    console.log(this.getAuthHeaders())
          console.log(response.body)
    return this.handleResponse<ChoiceRangeBooking>(response)
  }

  // ========== WALLET API ==========
  async getBalance(): Promise<ApiResponse<{ balance: number }>> {
    const response = await fetch(`${API_BASE_URL}/wallet/balance`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<{ balance: number }>(response)
  }

  // ========== RESULTS APIs ==========
  async getSessionResults(sessionId: string): Promise<ApiResponse<{
    results: LotteryResult[]
    bookings: any
  }>> {
    const response = await fetch(`${API_BASE_URL}/results/list?sessionId=${sessionId}`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse(response)
  }

  async uploadResults(sessionId: string, results: { category: string; number: number }[]): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_BASE_URL}/results/upload`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sessionId, results }),
    })
    return this.handleResponse(response)
  }

  // ========== ADMIN APIs ==========
  async createDraw(sessionId: string): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_BASE_URL}/sessions/draw-results`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sessionId }),
    })
    return this.handleResponse(response)
  }

  async calculateWinnings(sessionId: string): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_BASE_URL}/admin/calculate-v2`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sessionId }),
    })
    return this.handleResponse(response)
  }

  async resetBooking(bookingId: string, bookingType: 'single' | 'rajshree' | 'choice'): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_BASE_URL}/admin/reset-booking`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ bookingId, bookingType }),
    })
    return this.handleResponse(response)
  }

  async resetSessionBookings(sessionId: string): Promise<ApiResponse<any>> {
    const response = await fetch(`${API_BASE_URL}/admin/reset-session-bookings`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ sessionId }),
    })
    return this.handleResponse(response)
  }

  // ========== USER MANAGEMENT APIs ==========
  async createUser(userData: CreateUserRequest): Promise<ApiResponse<User>> {
    const response = await fetch(`${API_BASE_URL}/users/create`, {
      method: 'POST',
      headers: this.getAuthHeaders(),
      body: JSON.stringify(userData),
    })
    return this.handleResponse<User>(response)
  }

  async getAllUsers(): Promise<ApiResponse<User[]>> {
    const response = await fetch(`${API_BASE_URL}/admin/users`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<User[]>(response)
  }

  async updateUserStatus(userId: string, isActive: boolean): Promise<ApiResponse<User>> {
    const response = await fetch(`${API_BASE_URL}/admin/users/${userId}/status`, {
      method: 'PUT',
      headers: this.getAuthHeaders(),
      body: JSON.stringify({ isActive }),
    })
    return this.handleResponse<User>(response)
  }

  // ========== REPORT APIs ==========
  async getSingleReport(sessionId?: string): Promise<ApiResponse<{ singles: SingleBooking[] }>> {
    const url = sessionId 
      ? `${API_BASE_URL}/report/single?sessionId=${sessionId}`
      : `${API_BASE_URL}/report/single`
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<{ singles: SingleBooking[] }>(response)
  }

  async getRajshreeReport(sessionId?: string): Promise<ApiResponse<{ rajshrees: RajshreeBooking[] }>> {
    const url = sessionId 
      ? `${API_BASE_URL}/report/rajshree?sessionId=${sessionId}`
      : `${API_BASE_URL}/report/rajshree`
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<{ rajshrees: RajshreeBooking[] }>(response)
  }

  async getChoiceReport(sessionId?: string): Promise<ApiResponse<{ choices: ChoiceBooking[] }>> {
    const url = sessionId 
      ? `${API_BASE_URL}/report/choice?sessionId=${sessionId}`
      : `${API_BASE_URL}/report/choice`
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<{ choices: ChoiceBooking[] }>(response)
  }

  async getChoiceRangeReport(sessionId?: string): Promise<ApiResponse<{ choices: ChoiceRangeBooking[] }>> {
    const url = sessionId 
      ? `${API_BASE_URL}/report/choice-range?sessionId=${sessionId}`
      : `${API_BASE_URL}/report/choice-range`
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse<{ choices: ChoiceRangeBooking[] }>(response)
  }

  // ========== USER BOOKING HISTORY ==========
  async getUserBookingHistory(limit?: number): Promise<ApiResponse<{
    singles: SingleBooking[]
    rajshrees: RajshreeBooking[]
    choices: ChoiceBooking[]
  }>> {
    const url = limit 
      ? `${API_BASE_URL}/bookings/history?limit=${limit}`
      : `${API_BASE_URL}/bookings/history`
    
    const response = await fetch(url, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse(response)
  }

  // ========== ANALYTICS APIs ==========
  async getDashboardStats(): Promise<ApiResponse<{
    totalUsers: number
    totalRevenue: number
    activeLotteries: number
    pendingResults: number
    todayRevenue: number
    totalBets: number
    totalWinners: number
    payoutRatio: number
  }>> {
    const response = await fetch(`${API_BASE_URL}/admin/dashboard-stats`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse(response)
  }

  async getRevenueAnalytics(): Promise<ApiResponse<{
    today: number
    thisWeek: number
    thisMonth: number
    thisYear: number
  }>> {
    const response = await fetch(`${API_BASE_URL}/admin/revenue-analytics`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse(response)
  }

  async getUserStatistics(): Promise<ApiResponse<{
    activeUsers: number
    newRegistrations: number
    totalTransactions: number
    averageBetAmount: number
  }>> {
    const response = await fetch(`${API_BASE_URL}/admin/user-statistics`, {
      headers: this.getAuthHeaders(),
    })
    return this.handleResponse(response)
  }
// Add these to your existing ApiService class

async drawResults(sessionId: string): Promise<ApiResponse<any>> {
  const response = await fetch(`${API_BASE_URL}/sessions/draw-results`, {
    method: 'POST',
    headers: this.getAuthHeaders(),
    body: JSON.stringify({ sessionId }),
  })
  return this.handleResponse(response)
}



async resetBookings(sessionId: string): Promise<ApiResponse<any>> {
  const response = await fetch(`${API_BASE_URL}/admin/reset-session-bookings`, {
    method: 'POST',
    headers: this.getAuthHeaders(),
    body: JSON.stringify({ sessionId }),
  })
  return this.handleResponse(response)
}

async resetSingleBooking(bookingId: string, bookingType: string): Promise<ApiResponse<any>> {
  const response = await fetch(`${API_BASE_URL}/admin/reset-booking`, {
    method: 'POST',
    headers: this.getAuthHeaders(),
    body: JSON.stringify({ bookingId, bookingType }),
  })
  return this.handleResponse(response)
}

// Reports APIs

}

export const apiService = new ApiService()

// Utility functions for direct API calls
export async function fetchReportData(type: 'single' | 'rajshree' | 'choice' | 'choice-range', sessionId?: string) {
  try {
    const params = new URLSearchParams();
    if (sessionId) params.append('sessionId', sessionId);
    
    const response = await fetch(`${API_BASE_URL}/report/${type}?${params.toString()}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch ${type} report`);
    }
    
    const data = await response.json();
    return type === 'single' ? data.singles : 
           type === 'rajshree' ? data.rajshrees : 
           type === 'choice-range' ? data.choices : 
           data.choices;
  } catch (error) {
    console.error(`Error fetching ${type} report:`, error);
    return [];
  }
}

export async function fetchResultsList(sessionId: string) {
  try {
    const response = await fetch(`${API_BASE_URL}/results/list?sessionId=${sessionId}`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    
    if (!response.ok) {
      throw new Error('Failed to fetch results list');
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching results list:', error);
    return { results: [], bookings: { singles: [], rajshrees: [], choices: [] } };
  }
}

export async function fetchSessions() {
  try {
    const response = await fetch(`${API_BASE_URL}/sessions/list`, {
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
      },
    });
    if (!response.ok) throw new Error('Failed to fetch sessions');
    const data = await response.json();
    return data.sessions || [];
  } catch (error) {
    console.error('Error fetching sessions:', error);
    return [];
  }
}

